import React from "react";
import { Link } from "gatsby";
import {
  FaArrowRight,
  FaBolt,
  FaBriefcase,
  FaCloud,
  FaCogs,
  FaDesktop,
  FaFileAlt,
  FaLock,
  FaMicrosoft,
  FaShieldAlt,
  FaTasks,
} from "react-icons/fa";

const coreFeatures = [
  {
    icon: <FaMicrosoft size={22} />,
    title: "Microsoft 365 Setup & Migration",
    text:
      "Tenant setup, email migration, licensing, MFA, secure collaboration and user onboarding.",
  },
  {
    icon: <FaCloud size={22} />,
    title: "Azure Cloud & Managed Support",
    text:
      "Cloud administration, backup, monitoring, optimisation and operational support.",
  },
  {
    icon: <FaShieldAlt size={22} />,
    title: "Security & Compliance Readiness",
    text:
      "Practical controls for identity, data protection, audit readiness and assurance conversations.",
  },
  {
    icon: <FaDesktop size={22} />,
    title: "Managed IT & End User Support",
    text:
      "Remote and on-site support for Microsoft-heavy workplaces that need reliable day-to-day help.",
  },
];

const extendedFeatures = [
  "SharePoint information architecture, intranet and document management",
  "Teams governance, channels, permissions and collaboration standards",
  "OneDrive migration, sharing controls and file ownership clean-up",
  "Power Platform workflow automation and business process improvement",
  "Microsoft Intune device management and endpoint policy support",
  "Microsoft Defender hardening, alert review and security uplift",
  "Microsoft Purview, retention, sensitivity and data governance planning",
  "CloudLogic Career AI and custom business application advisory",
];

const resourceLinks = [
  {
    icon: <FaTasks size={20} />,
    title: "Explore Core Services",
    text:
      "See the Microsoft cloud, security, governance and managed IT services CloudLogic provides.",
    to: "/services",
    cta: "View Services",
  },
  {
    icon: <FaFileAlt size={20} />,
    title: "Book a Microsoft 365 Health Check",
    text:
      "Start with a practical review of your tenant, licensing, collaboration, security and governance.",
    to: "/contact",
    cta: "Request Health Check",
  },
  {
    icon: <FaBriefcase size={20} />,
    title: "Discuss Workflow or Product Ideas",
    text:
      "Talk to CloudLogic about automating internal processes or building Microsoft-integrated tools.",
    to: "/contact",
    cta: "Start Conversation",
  },
  {
    icon: <FaFileAlt size={20} />,
    title: "CloudLogic Career AI",
    text:
      "Discover the released career application platform for tailored resumes, cover letters and role-focused application guidance.",
    to: "/solutions/career-application-platform",
    cta: "View Platform",
  },
];

const CapabilitiesMatrix = ({ compact = false }) => {
  return (
    <section className={`capabilities-area ${compact ? "is-compact" : ""}`}>
      <div className="container">
        <div className="section-title capabilities-title">
          <span className="sub-title">Features & Extended Capabilities</span>
          <h2>Clear Services, Practical Extensions, and Direct Next Steps</h2>
          <p>
            CloudLogic should not look like a generic IT support website. The
            offer needs to feel concrete: what you can implement now, what can
            be extended later, and how a buyer can take the next step without
            decoding technical language.
          </p>
        </div>

        <div className="capability-feature-grid">
          {coreFeatures.map((feature) => (
            <article className="capability-feature-card" key={feature.title}>
              <div className="capability-feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </article>
          ))}
        </div>

        <div className="capability-extended-layout">
          <div className="capability-extended-copy">
            <span className="capability-mini-label">
              Extended Microsoft Cloud Features
            </span>
            <h3>For Organisations That Need More Than Basic IT Support</h3>
            <p>
              Once the Microsoft foundation is working, CloudLogic can help
              improve collaboration, automate repeatable work, reduce manual
              administration, and build clearer governance around people, files,
              devices, data and business systems.
            </p>
            <div className="capability-proof-note">
              <FaLock size={18} />
              <span>
                The message is simple for buyers: CloudLogic can build the
                foundation, improve adoption, secure the environment, and stay
                involved as a practical technology advisor.
              </span>
            </div>
          </div>

          <div className="capability-extended-list">
            {extendedFeatures.map((item) => (
              <div className="capability-extended-item" key={item}>
                <FaBolt size={14} />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="capability-link-grid" aria-label="CloudLogic links">
          {resourceLinks.map((link) => (
            <article className="capability-link-card" key={link.title}>
              <div className="capability-link-icon">{link.icon}</div>
              <h3>{link.title}</h3>
              <p>{link.text}</p>
              <Link to={link.to}>
                {link.cta}
                <FaArrowRight size={13} />
              </Link>
            </article>
          ))}
        </div>

        <div className="capability-bottom-cta">
          <div>
            <FaCogs size={24} />
            <p>
              Need Microsoft 365, Azure, SharePoint, Teams, automation, security
              or compliance support?
            </p>
          </div>
          <Link to="/contact" className="default-btn">
            Book Microsoft 365 Health Check
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CapabilitiesMatrix;
